import { imageUrl } from "@/lib/api";
import { useI18n } from "@/context/LangContext";
import { pickLocalized, labelType, labelCondition } from "@/lib/i18n";
import { MapPin, Calendar, PencilSimple, Trash } from "@phosphor-icons/react";

const CONDITION_COLORS = {
  Excellent: "bg-[#e8efdc] text-[#3f5119] border-[#c9d6a8]",
  Good: "bg-[#eef4ea] text-[#3f5119] border-[#d1e0c1]",
  Fair: "bg-[#fbeee0] text-[#8a5a1e] border-[#eed2a8]",
  Poor: "bg-[#fbe4de] text-[#8a2b1e] border-[#eec3b8]",
  Critical: "bg-[#f5d5cf] text-[#7a1a10] border-[#e0a89e]",
};

export default function HeritageCard({ item, user, onEdit, onDelete, onOpen }) {
  const { lang, t } = useI18n();
  const canEdit = !!user;
  const name = pickLocalized(item, "name", lang);
  const description = pickLocalized(item, "description", lang);
  const location = pickLocalized(item, "location", lang);
  const cover = (item.image_urls && item.image_urls[0]) || item.image_url;
  const photoCount = (item.image_urls && item.image_urls.length) || (item.image_url ? 1 : 0);
  return (
    <article
      className="group flex flex-col bg-white border border-[#e5e3db] hover:-translate-y-1 hover:shadow-sm transition-transform duration-200 fade-up"
      data-testid={`heritage-card-${item.id}`}
    >
      <button
        type="button"
        onClick={() => onOpen?.(item)}
        className="relative aspect-[4/3] overflow-hidden bg-[#efece4] text-left"
        data-testid={`heritage-card-open-${item.id}`}
      >
        {cover ? (
          <img
            src={imageUrl(cover)}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#b7b0a0] serif italic text-lg">
            No image
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className="inline-block bg-white/95 border border-[#e5e3db] px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#1a1a1a]">
            {labelType(item.type, lang)}
          </span>
        </div>
        {photoCount > 1 && (
          <div className="absolute top-3 right-3 bg-black/60 text-white text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1">
            {photoCount} {t("card.photos")}
          </div>
        )}
      </button>

      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <h3
            className="serif text-xl font-bold text-[#1a1a1a] leading-tight cursor-pointer hover:text-[#b25a38] transition-colors"
            onClick={() => onOpen?.(item)}
          >
            {name}
          </h3>
          <span
            className={`text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-1 border ${
              CONDITION_COLORS[item.condition] || CONDITION_COLORS.Good
            }`}
          >
            {labelCondition(item.condition, lang)}
          </span>
        </div>

        <p className="text-sm text-[#5c5c5c] mt-2 line-clamp-2 leading-relaxed">
          {description}
        </p>

        <div className="mt-4 space-y-1.5 text-xs text-[#5c5c5c]">
          <div className="flex items-center gap-2">
            <MapPin size={14} weight="duotone" className="text-[#b25a38]" />
            <span className="truncate">{location || t("card.locationTBD")}</span>
          </div>
          {item.year_built && (
            <div className="flex items-center gap-2">
              <Calendar size={14} weight="duotone" className="text-[#b25a38]" />
              <span>{t("card.built")} {item.year_built}</span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-[#e5e3db] flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#5c5c5c]">
            {item.local_authority || "—"}
          </div>
          {canEdit && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onEdit?.(item)}
                className="h-8 w-8 flex items-center justify-center border border-transparent text-[#5c5c5c] hover:text-[#b25a38] hover:border-[#e5e3db] transition-colors"
                data-testid={`heritage-edit-${item.id}`}
                aria-label="Edit"
              >
                <PencilSimple size={15} weight="duotone" />
              </button>
              <button
                onClick={() => onDelete?.(item)}
                className="h-8 w-8 flex items-center justify-center border border-transparent text-[#5c5c5c] hover:text-[#d32f2f] hover:border-[#e5e3db] transition-colors"
                data-testid={`heritage-delete-${item.id}`}
                aria-label="Delete"
              >
                <Trash size={15} weight="duotone" />
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
